import { MouseEvent, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Toolbar,
  MenuItem,
  Menu,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ExtensionOutlined, Logout } from '@mui/icons-material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { StyledAppBar, StyledMenuItem } from './styles';
import NewNoteButton from './NewNoteButton';
import { WhiteLogoIcon } from '@/components/Icons/Icons';
import logoEmblem from '@/assets/NoteGoLogo_Emblem_Reverse.png';
import { RouteMap } from '@/routes/Routes';
import { useAuthStore } from '@/stores/authStore';
import { setLogoutFlag } from '@/utils/crossTabAuthSync';
import getGravatarUrl from '@/helpers/getGravatarUrl';
import useAvatarStore from '@/stores/changeAvatarStore';
import DynamicBanner from '../Banner/DynamicBanner';
import { useExtensionManager } from '@/hooks/useExtensionManager';
import { useAuthProtection } from '@/features/auth/useAuthProtection';

export const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // 900px and below
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const { currentUser } = useAuthStore();
  const { avatarChanged, setAvatarChanged } = useAvatarStore();
  const { isExtensionInstalled, checkExtensionInstalled } = useExtensionManager();
  const [isExtension, setIsExtension] = useState(false);
  const { canCreateNote } = useAuthProtection();

  // Derive avatar source from currentUser - no need for separate state
  const avatarSrc = useMemo(
    () => currentUser?.photoURL || getGravatarUrl(currentUser?.email || ''),
    [currentUser?.photoURL, currentUser?.email],
  );

  // Reset avatarChanged flag when it's been processed
  useEffect(() => {
    if (avatarChanged) {
      setAvatarChanged(false);
    }
  }, [avatarChanged, setAvatarChanged]);

  useEffect(() => {
    const checkExtension = async () => {
      if (canCreateNote) {
        const isInstalled = await isExtensionInstalled();
        setIsExtension(isInstalled);
      }
    };

    checkExtension();
  }, [isExtensionInstalled, canCreateNote]);

  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsClick = () => {
    navigate(RouteMap.SETTINGS);
    handleMenuClose();
  };

  const handleChromeExtensionClick = () => {
    if (isExtension) {
      checkExtensionInstalled();
    } else {
      const url = import.meta.env.VITE_CHROME_EXTENSION_URL;
      window.open(url, '_blank');
    }
    handleMenuClose();
  };

  const handleLogoClick = () => {
    navigate(RouteMap.ROOT);
  };

  const handleSignOut = () => {
    // Clear session storage to prevent redirect after login
    sessionStorage.removeItem('redirectAfterLogin');
    // Set logout flags BEFORE navigation to prevent race condition where
    // Login's redirect effect sees userLoggedIn=true and navigates back
    sessionStorage.setItem('userRequestedLogout', 'true');
    setLogoutFlag();
    // Navigate to login page with logout flag - Login page handles all cleanup
    // including doSignOut() and Chrome extension auth clearing
    navigate(`${RouteMap.LOGIN}?logout=true`);
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id="primary-search-account-menu"
      keepMounted
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      sx={{ marginTop: '8px' }}
    >
      <StyledMenuItem dense disableRipple>
        {currentUser?.displayName && <Typography variant="body2">{currentUser?.displayName}</Typography>}
        {currentUser?.email && (
          <Typography variant="body2" color="textSecondary">
            {currentUser?.email}
          </Typography>
        )}
      </StyledMenuItem>
      <Divider />
      <MenuItem dense onClick={handleSettingsClick}>
        <ListItemIcon>
          <SettingsOutlinedIcon />
        </ListItemIcon>
        <ListItemText>Settings</ListItemText>
      </MenuItem>

      <MenuItem dense onClick={handleChromeExtensionClick}>
        <ListItemIcon>
          <ExtensionOutlined />
        </ListItemIcon>
        <ListItemText>{isExtension ? 'Open NoteGo Extension' : 'Install NoteGo Extension'}</ListItemText>
      </MenuItem>

      <Divider />
      <MenuItem data-testid="logout-button" dense onClick={handleSignOut}>
        <ListItemIcon>
          <Logout />
        </ListItemIcon>
        <ListItemText>Log out</ListItemText>
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <StyledAppBar>
        <DynamicBanner />
        <Toolbar sx={{ minHeight: isMobile ? 56 : 64, px: isMobile ? 1 : 3 }}>
          <IconButton onClick={handleLogoClick} sx={{ p: isMobile ? 0.5 : 1 }}>
            {isMobile ? (
              <img src={logoEmblem} alt="NoteGo" style={{ width: '32px', height: '32px' }} />
            ) : (
              <WhiteLogoIcon sx={{ width: '204.8px', height: '32px' }} />
            )}
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <Typography
              sx={{
                color: '#fff',
                fontSize: isMobile ? '12px' : '16px',
                fontStyle: 'italic',
                fontWeight: 400,
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
              }}
            >
              Stop explaining… Start showing
            </Typography>
          </Box>
          <NewNoteButton />
          <Box sx={{ display: 'flex' }}>
            <Avatar
              data-testid="avatar-menu"
              sx={{ cursor: 'pointer' }}
              onClick={handleProfileMenuOpen}
              src={avatarSrc}
            >
              {currentUser?.displayName?.charAt(0)}
            </Avatar>
          </Box>
        </Toolbar>
      </StyledAppBar>
      {renderMenu}
    </Box>
  );
};
